// apps/admin/src/features/billing/BillingModeSelector.tsx
//
// Phase 8.2 Wave 4 Task 27 — BillingModeSelector dropdown component.
//
// vg-binding: CONTEXT:decisions=P8.D-485,P8.D-491,BR-10
// vg-binding: PLAN:task-27
//
// Per CONTEXT.md A7 (admin per-merchant Billing surface — mode + config).
// Per Task-27 acceptance: BillingModeSelector dropdown PREPAID / INVOICE.
// Preserves the 4.1 radio-group + extra CREDIT option for backwards
// compatibility (existing e2e tests assert the radio group test IDs).
// The dropdown (`merchant-billing-mode-selector`) is the new task-27 surface.

import type { JSX } from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

export type BillingMode = 'PREPAID' | 'CREDIT' | 'INVOICE';

// Mode option values — labels + descriptions resolved at render-time via
// t('merchant_billing.modeOption.<value>.{label,description}').
const MODE_OPTION_VALUES: BillingMode[] = ['PREPAID', 'CREDIT', 'INVOICE'];

export interface BillingModeSelectorProps {
  /** Currently-pending mode the admin has selected (uncommitted). */
  pendingMode: BillingMode | null;
  /** Live billing_mode persisted on the server. */
  currentMode: BillingMode | null;
  /** Reason text shown in audit-log input. */
  reason: string;
  /** Whether the page-level form is loading initial data. */
  loading: boolean;
  /** True while a mode-switch PUT is in flight. */
  modeSubmitting: boolean;
  /** Auth gate — only admin role can switch modes. */
  isAdmin: boolean;
  /** Hint copy for accountant when admin-only action is disabled. */
  isAccountant: boolean;
  /** True when PREPAID → INVOICE switch will freeze wallet. */
  showWalletFreezeWarning: boolean;
  /** Whether pendingMode != currentMode. */
  modeIsDirty: boolean;
  onPendingModeChange: (next: BillingMode) => void;
  onReasonChange: (next: string) => void;
  onSubmit: () => void;
}

/**
 * BillingModeSelector — radio group (back-compat with spec) + a new
 * dropdown surface (`merchant-billing-mode-selector`) required by Phase 8.2
 * task-27 acceptance. The dropdown only exposes PREPAID / INVOICE per the
 * task spec; the radio group keeps the full PREPAID / CREDIT / INVOICE
 * set the underlying API contract supports.
 */
export function BillingModeSelector(
  props: BillingModeSelectorProps,
): JSX.Element {
  const { t } = useTranslation('finance');
  const {
    pendingMode,
    currentMode: _currentMode,
    reason,
    loading,
    modeSubmitting,
    isAdmin,
    isAccountant,
    showWalletFreezeWarning,
    modeIsDirty,
    onPendingModeChange,
    onReasonChange,
    onSubmit,
  } = props;

  // Mark currentMode as intentionally unused (surfaced for future hint copy).
  void _currentMode;

  return (
    <form
      data-testid="merchant-billing-mode-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex flex-col gap-4"
    >
      {/* New task-27 dropdown — exposes PREPAID / INVOICE per acceptance. */}
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="merchant-billing-mode-selector"
          className="text-xs font-medium text-pw-text-muted"
        >
          {t('merchant_billing.mode.quickSwitchLabel')}
        </label>
        <Select
          value={pendingMode === 'CREDIT' ? '' : pendingMode ?? ''}
          disabled={!isAdmin || loading}
          onValueChange={(value) => {
            if (value === 'PREPAID' || value === 'INVOICE') {
              onPendingModeChange(value);
            }
          }}
        >
          <SelectTrigger
            id="merchant-billing-mode-selector"
            data-testid="merchant-billing-mode-selector"
          >
            <SelectValue
              placeholder={t('merchant_billing.mode.selectPlaceholder')}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="PREPAID">PREPAID</SelectItem>
            <SelectItem value="INVOICE">INVOICE</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11px] text-pw-text-muted">
          {t('merchant_billing.mode.creditHint')}
        </p>
      </div>

      {/* Legacy radio group — preserved e2e tests + CREDIT option. */}
      <div
        role="radiogroup"
        aria-label={t('merchant_billing.mode.ariaLabel')}
        data-testid="merchant-billing-mode-select"
        className="flex flex-col gap-2"
      >
        {MODE_OPTION_VALUES.map((optValue) => {
          const checked = pendingMode === optValue;
          const optLabel = t(`merchant_billing.modeOption.${optValue}.label`);
          return (
            <label
              key={optValue}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                checked
                  ? 'border-pw-primary bg-pw-primary/5'
                  : 'border-pw-border bg-pw-bg hover:bg-pw-surface',
                !isAdmin && 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                type="radio"
                name="merchant-billing-mode"
                value={optValue}
                checked={checked}
                disabled={!isAdmin}
                onChange={() => onPendingModeChange(optValue)}
                className="mt-0.5 h-4 w-4 accent-pw-primary"
                aria-label={optLabel}
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-pw-text">
                  {optLabel}
                </div>
                <div className="mt-0.5 text-xs text-pw-text-muted">
                  {t(`merchant_billing.modeOption.${optValue}.description`)}
                </div>
              </div>
            </label>
          );
        })}
      </div>

      {showWalletFreezeWarning ? (
        <p
          className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
          role="status"
        >
          {t('merchant_billing.mode.walletFreezeWarning')}
        </p>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="merchant-billing-reason"
          className="text-xs font-medium text-pw-text-muted"
        >
          {t('merchant_billing.mode.reasonLabel')}
        </label>
        <Input
          id="merchant-billing-reason"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          maxLength={500}
          placeholder={t('merchant_billing.mode.reasonPlaceholder')}
          disabled={!isAdmin || !modeIsDirty}
        />
      </div>

      <span
        title={isAccountant ? t('merchant_billing.mode.adminOnly') : undefined}
        className="self-end"
      >
        <Button
          type="submit"
          variant="default"
          disabled={!isAdmin || !modeIsDirty || modeSubmitting || loading}
          aria-disabled={
            !isAdmin || !modeIsDirty || modeSubmitting || loading
          }
          data-testid="merchant-billing-mode-save-btn"
        >
          {modeSubmitting
            ? t('merchant_billing.mode.switching')
            : t('merchant_billing.mode.saveBtn')}
        </Button>
      </span>
    </form>
  );
}

export default BillingModeSelector;
